import errorHandlers from '@/services/error-handlers';
import http from '@/services/http';
import { filterMenu, filterExclusiveMenuItems } from '@/helpers/filter-menu';
import parseSchedule from '@/helpers/parse-schedule';
import storage from '@/services/storage';



export default {
  namespaced: true,

  state: {
    attributes: [],
    categories: [],
    filters: [],
    items: [],
    loadStatus: null,
    menuId: null,
    menuRequestDetails: {},
    menuType: null,
    menuTypeOverride: null,
    schedule: [],
    selectedCategoryId: null,
    selectedFavoriteItemId: null,
    selectedFilterIds: [],
    selectedItemId: null,
    selectedItemIsUpsell: false,
    selectedOrderItemId: null,
    showSecretMenu: false,
    unavailablePerSchedule: false
  },

  getters: {
    filterAttributes(state) {
      const all = [];
      const groupsByType = {};

      state.filters.forEach((filter) => {
        if (filter.type !== 'ThirdPartyRestriction') {
          groupsByType[filter.type] = {
            ...filter,
            attributes: []
          };
        }
      });

      state.attributes.forEach((attr) => {
        const group = groupsByType[attr.type];

        const id = `${attr.type}-${attr.name}`;

        const isSelected = state.selectedFilterIds.includes(id);

        const attribute = {
          ...attr,
          id,
          isSelected
        };

        if (group) {
          Object.assign(attribute, { isExclusion: group.isExclusion });

          group.attributes.push(attribute);
        }

        if (attr.type !== 'ThirdPartyRestriction') {
          all.push(attribute);
        }
      });

      const groups = [];

      Object.values(groupsByType).forEach((group) => {
        if (group.attributes.length > 0) {
          groups.push(group);
        }
      });

      const selected = all.filter(attribute => attribute.isSelected);

      return {
        all,
        groups,
        selected
      };
    },

    menu(state, getters, rootState, rootGetters) {
      const { isEnrolledInLoyalty } = rootGetters['session/currentUser'];
      const { isSignedIn } = rootGetters['session/session'];
      const merchant = rootGetters['merchant/merchant'];

      const categories = state.categories.filter(category => state.showSecretMenu || category.categoryTemplate !== 'SecretCategoryTemplate');

      const favoriteItems = rootGetters['favoriteItems/availableFavoriteItems'];

      if (favoriteItems.length > 0 && !merchant.features['hide-favorites']) {
        categories.unshift({
          categoryId: -1,
          categoryTemplate: 'FavoritesCategoryTemplate',
          description: null,
          displayName: 'My Favorite Items',
          menuItems: favoriteItems
        });
      }

      const filteredCategoriesBySelection = filterMenu(categories, getters.filterAttributes.selected);
      const filteredCategories = filterExclusiveMenuItems(filteredCategoriesBySelection, isEnrolledInLoyalty, isSignedIn);

      const menu = {
        ...state,
        categories,
        filteredCategories
      };

      return menu;
    },

    menuSchedule(state, getters, rootState, rootGetters) {
      const selectedLocation = rootGetters['locations/selectedLocation'];
      return parseSchedule(state.schedule, selectedLocation.timeZone);
    },

    menuTypeDisplay(state) {
      const { menuType } = state;
      let display = menuType.replace(/([A-Z]+)/g, ' $1').trim();

      if (display === 'Take Out') {
        display = 'Pickup';
      }

      return display;
    },

    selectedCategory(state) {
      const category = state.categories.find(c => c.categoryId === state.selectedCategoryId);

      return category;
    },

    selectedFavoriteItem(state, getters, rootState, rootGetters) {
      const favoriteItems = rootGetters['favoriteItems/favoriteItems'];

      const favoriteItem = favoriteItems.items.find(item => item.favoriteItemId === state.selectedFavoriteItemId);

      return favoriteItem;
    },

    selectedItem(state, getters) {
      // For a "standard" menu item, there should be a match for the `selectedItemId` and `menuItemId` since the user had selected an item
      // that exists on the menu. However, for "dynamic" menu items, they only exist as a modifier on a menu item. Though they are rendered as
      // modifiers of a menu item, they are still actually considered menu items, their rendering however does not follow the standard ui of being rendered as
      // an actual menu item.
      // So, find the `selectedItemId` within `itemGroups` and build up the selected item data if the selected item happens to be "dynamic" menu item.
      // NOTE: "dynamic" menu item means that a menu items can have a top-level modifier group that determines the rendering of all the subsequent modifer groups.
      // So, a large sandwich might have different "dynamically" rendered toppings and or add-ons compared to a medium or small.
      const item = state.items.find(i => i.menuItemId === state.selectedItemId);

      if (item) {
        return {
          ...item,
          favoriteItemId: getters.selectedFavoriteItem ? getters.selectedFavoriteItem.favoriteItemId : null,
          isUpsell: state.selectedItemIsUpsell,
          orderItemId: state.selectedOrderItemId
        };
      }

      const matchedItemWithItemGroups = state.items.find((_item) => {
        if ('itemGroups' in _item) {
          return _item.itemGroups[state.selectedItemId];
        }

        return null;
      });

      if (matchedItemWithItemGroups) {
        const dynamicModifierGroup = matchedItemWithItemGroups.modifierGroups.find(modGroup => modGroup.isVirtual) || null;

        const dynamicModifier = dynamicModifierGroup
          ? dynamicModifierGroup.modifiers.find(mod => mod.menuItemGroupItemId === state.selectedItemId)
          : null;

        return {
          ...matchedItemWithItemGroups,
          favoriteItemId: getters.selectedFavoriteItem ? getters.selectedFavoriteItem.favoriteItemId : null,
          isUpsell: state.selectedItemIsUpsell,
          orderItemId: state.selectedOrderItemId,
          dynamicModifier
        };
      }

      return null;
    },

    selectedOrderItem(state, getters, rootState, rootGetters) {
      const order = rootGetters['orders/order'];

      const orderItems = order.orderItems.concat(order.orderItemsStaged);

      const orderItem = orderItems.find(item => item.orderItemId === state.selectedOrderItemId);

      return orderItem;
    }
  },

  actions: {
    async getMenu({ commit, dispatch, state }, { forceLoad, silent } = {}) {
      try {
        await dispatch('getMenuRequestDetails');

        if (!state.menuRequestDetails.alreadyLoaded || forceLoad) {
          if (!silent) commit('SET_LOAD_STATUS', 'loading');

          let url = `/cardfree-api/v1/menu/${state.menuRequestDetails.menuId}?menu-type=${state.menuRequestDetails.menuType}`;

          if (state.menuRequestDetails.menuVerbosity) {
            state.menuRequestDetails.menuVerbosity.forEach((verbosity) => {
              url += `&verbosity=${verbosity}`;
            });
          }

          const { data } = await http.get(url);

          commit('SET_LOAD_STATUS', 'loaded');

          commit('SET_MENU', {
            attributes: data.attributes,
            categories: data.categories,
            filters: data.filters,
            items: data.categories.flatMap(category => category.menuItems),
            menuId: data.menuId,
            menuType: data.menuType,
            schedule: data.schedule,
            unavailablePerSchedule: data.unavailablePerSchedule
          });

          dispatch('updateSelectedFilters', []);

          await dispatch('favoriteItems/getFavoriteItems', { silentError: true }, { root: true });
        }
      }

      catch (error) {
        errorHandlers.silent(error);

        commit('SET_LOAD_STATUS', 'error');

        commit('UNSET_MENU');
      }
    },

    getMenuRequestDetails({ commit, rootGetters, state }) {
      const merchant = rootGetters['merchant/merchant'];
      const selectedLocation = rootGetters['locations/selectedLocation'];
      const order = rootGetters['orders/order'];
      const orderDetails = rootGetters['session/orderDetails'];

      const menuId = selectedLocation.storeId || 0; // NOTE: Menu ID 0 = master menu

      /* secret menu */
      const showSecretMenu = storage.session.get('showSecretMenu');
      commit('SET_SHOW_SECRET_MENU', showSecretMenu);

      /* menu verbosity */
      let menuVerbosity;
      if (merchant.features['menu-verbosity']) {
        menuVerbosity = merchant.features['menu-verbosity'].split(',').map(verbosity => verbosity.trim());
      }

      /* menu type */
      let menuType = 'DineIn';

      if (state.menuTypeOverride) {
        menuType = state.menuTypeOverride;
      }
      else if (orderDetails.menuType) {
        menuType = orderDetails.menuType;
      }
      else if (orderDetails.orderType === 'catering') {
        menuType = 'Catering';
      }
      else if (orderDetails.fulfillmentType === 'delivery') {
        menuType = 'Delivery';
      }
      else if (orderDetails.fulfillmentType === 'inherit') {
        menuType = order.menuType;
      }
      else if (orderDetails.fulfillmentType === 'pick-up') {
        menuType = 'TakeOut';
      }
      else if (orderDetails.fulfillmentType === 'room-service') {
        menuType = 'RoomService';
      }
      else if (orderDetails.fulfillmentType === 'shipping') {
        menuType = 'Shipping';
      }

      /*
        NOTE: If we ended up with an undefined menu type, we don't want to load a menu.
        So we set alreadyLoaded to true in that case to skip menu load until we have
        a valid menuType to load.
      */

      const alreadyLoaded = !menuType || (state.menuRequestDetails.menuId === menuId && state.menuRequestDetails.menuType === menuType);

      commit('SET_MENU_REQUEST_DETAILS', {
        alreadyLoaded,
        menuId,
        menuType,
        menuVerbosity
      });
    },

    selectCategory({ commit }, categoryId) {
      commit('SET_SELECTED_CATEGORY', categoryId);
    },

    selectItem({ commit }, options = {}) {
      commit('SET_SELECTED_ITEM', options);
    },

    selectMenuType({ commit, dispatch }, type) {
      if (type === 'catering') {
        dispatch('orders/normalizeOrderDetails', {
          defaultFulfillmentType: 'pick-up',
          defaultOrderType: 'catering',
          orderTypes: ['catering']
        }, {
          root: true
        });
      }

      else if (type === 'secret') {
        dispatch('orders/normalizeOrderDetails', {
          defaultFulfillmentType: 'pick-up',
          defaultOrderType: 'standard',
          orderTypes: ['group', 'standard']
        }, {
          root: true
        });

        storage.session.set('showSecretMenu', true);
        commit('SET_SHOW_SECRET_MENU', true);
      }

      else if (type === 'standard') {
        dispatch('orders/normalizeOrderDetails', {
          defaultFulfillmentType: 'pick-up',
          defaultOrderType: 'standard',
          orderTypes: ['group', 'standard']
        }, {
          root: true
        });
      }

      dispatch('getMenu');
    },

    unsetSelectedFavoriteItem({ commit }) {
      commit('SET_SELECTED_FAVORITE_ITEM', null);
    },

    updateSelectedFilters({ commit }, ids) {
      commit('SET_FILTER_IDS', ids);
    }
  },

  mutations: {
    SET_FILTER_IDS(state, ids) {
      state.selectedFilterIds = ids || [];
    },

    SET_LOAD_STATUS(state, status) {
      state.loadStatus = status;
    },

    SET_MENU(state, menu) {
      state.attributes = menu.attributes || [];
      state.categories = menu.categories || [];
      state.filters = menu.filters || [];
      state.items = menu.items;
      state.menuId = menu.menuId;
      state.menuType = menu.menuType;
      state.schedule = menu.schedule || [];
      state.unavailablePerSchedule = menu.unavailablePerSchedule || false;
    },

    SET_MENU_REQUEST_DETAILS(state, details) {
      state.menuRequestDetails = details || {};
    },

    SET_MENU_TYPE_OVERRIDE(state, menuTypeOverride) {
      state.menuTypeOverride = menuTypeOverride;
    },

    SET_SELECTED_CATEGORY(state, id) {
      state.selectedCategoryId = Number(id) || null;
    },

    SET_SELECTED_FAVORITE_ITEM(state, id) {
      state.selectedFavoriteItemId = Number(id) || null;
    },

    SET_SELECTED_ITEM(state, options) {
      state.selectedFavoriteItemId = Number(options.favoriteItemId) || null;
      state.selectedItemId = Number(options.itemId);
      state.selectedItemIsUpsell = options.isUpsell || false;
      state.selectedOrderItemId = options.orderItemId || null;
    },

    SET_SHOW_SECRET_MENU(state, showSecretMenu) {
      state.showSecretMenu = showSecretMenu;
    },

    UNSET_MENU(state) {
      state.attributes = [];
      state.categories = [];
      state.filters = [];
      state.items = [];
      state.menuId = null;
      state.menuType = null;
      state.schedule = [];
      state.unavailablePerSchedule = false;
    }
  }
};
